import moment from 'moment';
import PropTypes from 'prop-types';
import * as React from 'react';
import Picker, { KINDS_CLASSMAP } from './Picker';

type Props = (
    | {
          date: string; // YYYY-MM-DD
          onChange: (date: string) => void;
          formatPrefix?: (date: string) => React.ReactNode;
          isClearable: false;
      }
    | {
          date: string | undefined;
          onChange: (date: string | undefined) => void;
          formatPrefix?: (date: string | undefined) => React.ReactNode;
          isClearable: true;
      }
) & {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
    kind: keyof typeof KINDS_CLASSMAP;
    highlightedDates: string[];
    validRange: { from?: string; to?: string };
    disabled: boolean;
    readOnly: boolean;
    hasError: boolean;
    showIcon: boolean;
    placeholderText: string;
    className?: string;
    id?: string;
    isSmall?: boolean;
    dataIntercomTarget: string;
};

/**
 * Displays a date picker
 *
 * @prop {function} onChange Triggered when a new date (or no date) is selected
 * @prop {string | undefined} date Currently selected date. May only be `undefined` if `isClearable=true`
 * @prop {boolean?} isClearable If true, `undefined` is a valid date and no date can be selected without error message. Defaults to `false`
 * @prop {string?} kind Visual kind. Defaults to `DEFAULT`
 * @prop {Array<string>?} highlightedDates Dates that will be highlighted with a dot
 * @prop {DateRange?} validRange Dates outside of the validRange will be disabled
 * @prop {boolean?} disabled If true, the component is disabled. Defaults to `false`
 * @prop {boolean?} readOnly If true, the component is readonly. Defaults to `false`
 * @prop {boolean?} hasError If true, the error styles are applied. Defaults to `false`
 * @prop {showIcon?} showIcon If true, the arrow on the right is displayed. Defaults to `true`
 * @prop {string?} placeholderText Text that is displayed when the input field is empty. Defaults to `''`
 * @prop {string?} id Sets the id of the input field
 * @prop {function?} formatPrefix Callback that returns the string that should be printed before the input element
 */
export default class Wrapper extends React.Component<Props> {
    static propTypes = {
        onChange: PropTypes.func.isRequired,
        date: PropTypes.string,
        isClearable: PropTypes.bool,
        kind: PropTypes.oneOf(['DEFAULT', 'TITLE']),
        highlightedDates: PropTypes.arrayOf(PropTypes.string),
        validRange: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
        disabled: PropTypes.bool,
        readOnly: PropTypes.bool,
        hasError: PropTypes.bool,
        showIcon: PropTypes.bool,
        placeholderText: PropTypes.string,
        id: PropTypes.string,
        formatPrefix: PropTypes.func,
        isSmall: PropTypes.bool,
        dataIntercomTarget: PropTypes.string,
    };

    static defaultProps: Partial<Props> = {
        onFocus: () => undefined,
        isClearable: false,
        kind: 'DEFAULT',
        highlightedDates: [],
        validRange: {},
        disabled: false,
        readOnly: false,
        hasError: false,
        showIcon: true,
        placeholderText: '',
    };

    handleChange = (newDate?: moment.Moment) => {
        if (this.props.isClearable) {
            this.props.onChange(newDate && newDate.format(moment.HTML5_FMT.DATE));
        } else if (!!newDate) {
            this.props.onChange(newDate.format(moment.HTML5_FMT.DATE));
        }
    };

    formatPrefix = (date: moment.Moment | undefined) => {
        if (!this.props.formatPrefix) {
            return;
        }

        if (this.props.isClearable) {
            return this.props.formatPrefix(date && date.format(moment.HTML5_FMT.DATE));
        } else if (!!date) {
            return this.props.formatPrefix(date.format(moment.HTML5_FMT.DATE));
        } else {
            return undefined;
        }
    };

    render() {
        const {
            date,
            onFocus,
            isClearable,
            kind,
            highlightedDates,
            validRange,
            disabled,
            readOnly,
            hasError,
            showIcon,
            placeholderText,
            id,
            isSmall,
            dataIntercomTarget,
        } = this.props;
        return (
            <Picker
                onChange={this.handleChange}
                date={date ? moment(date, moment.HTML5_FMT.DATE) : undefined}
                onFocus={onFocus}
                isClearable={isClearable}
                kind={kind}
                highlightedDates={highlightedDates}
                validRange={validRange}
                disabled={disabled}
                readOnly={readOnly}
                hasError={hasError}
                showIcon={showIcon}
                placeholderText={placeholderText}
                id={id}
                formatPrefix={this.formatPrefix}
                isSmall={isSmall}
                dataIntercomTarget={dataIntercomTarget}
            />
        );
    }
}
